import { useState, useEffect } from "react";
import { Space, Button, Row, Col, Input, Form, notification, Typography, Select } from "antd";
import { LeftCircleOutlined } from "@ant-design/icons";
import { useGetUsersQuery } from "../../../redux/services/userApi";
import { useGetPartnersQuery } from "../../../redux/services/partnerApi";
import { useGetBrandsQuery } from "../../../redux/services/brandApi";
import {
  useCreateInstallationMutation,
  useLazyGetInstallationByIdQuery,
  useUpdateInstallationMutation
} from "../../../redux/services/installationAPI";
import { useNavigate, useParams } from "react-router-dom";
import { useLazyGetSurveyByIdQuery } from "../../../redux/services/surveyAPI";
import { useLazyGetBrandModelsQuery } from "../../../redux/services/brandModelApi";
import ErrorDetails from "../../../components/ErrorDetails";
import {
  AltContactNumberInputElement,
  BrandModelSelect,
  CheckBoxInput,
  CityManagerSelect,
  CitySelect,
  ContactNumberInputElement,
  CustomerCitySelect,
  DropDownSelectProps,
  InputElement,
  PincodeInputElement,
  StateSelect,
  ZoneSelect,
  NumberInputElement
} from "../common/FormsElements";
import { localizeDateTime, isUserHasRoleTypes } from "../../../utils";
import {
  yesNoOptions,
  categoryOptions,
  customerBillingOptions,
  natureOfWorkOptions,
  OldInstallTypeOptions,
  installTypeOptions,
  completionStatusOptions
} from "../../../constants/helperConstants";
import { useAppSelector } from "../../../redux/hooks";

const InstallationForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const onGoBack = () => {
    navigate(-1);
  };

  const installationId = params?.installationId;
  const surveyId = params?.surveyId;

  const isEditMode = installationId != null;

  const [getSurveyById, { data: surveyData }] = useLazyGetSurveyByIdQuery();
  const [getInstalltionById, { data: installationData }] = useLazyGetInstallationByIdQuery();

  const selectedInstallationData = installationData?.installation;
  const selectedSurveyData = surveyData?.survey;

  const partnerIdFromInstallation = selectedInstallationData?.install_partner?.id;
  const [partnerId, setPartnerId] = useState<string>();

  const [locationId, setLocationId] = useState(null);

  const { data: partnerData, refetch: refetchPartners } = useGetPartnersQuery({
    all_pages: true,
    location_id: locationId
  });

  const isOldIstallation =
    isEditMode &&
    selectedInstallationData &&
    selectedInstallationData.created_at &&
    new Date(selectedInstallationData.created_at) < new Date("2024-09-27");

  useEffect(() => {
    if (locationId) {
      refetchPartners();
    }
  }, [locationId]);

  useEffect(() => {
    if (surveyId) {
      getSurveyById(surveyId);
    }
  }, [surveyId]);

  useEffect(() => {
    if (installationId) {
      getInstalltionById(installationId);
    }
  }, [installationId]);

  useEffect(() => {
    setLocationId(isEditMode ? selectedInstallationData?.city?.id : selectedSurveyData?.city?.id);
  }, [selectedInstallationData, selectedSurveyData]);

  const onCityChange = (cityLocationId: any) => {
    setLocationId(cityLocationId);
    form.setFieldValue("install_partner_id", undefined);
  };

  const { data: fieldAgentData } = useGetUsersQuery({
    role_type: "field_agent",
    partner_id: partnerId || partnerIdFromInstallation,
    all_pages: true
  });
  const fieldAgents = partnerId || partnerIdFromInstallation ? fieldAgentData?.users || [] : [];

  const { data: partnerManagerData } = useGetUsersQuery({
    role_type: "partner_manager",
    partner_id: partnerId || partnerIdFromInstallation
  });
  const partnerManager =
    partnerId || partnerIdFromInstallation ? partnerManagerData?.users || [] : [];

  const { data: brandData } = useGetBrandsQuery({ all_pages: true });
  const brands: any = brandData?.brands || [];

  let brandModels: any = [];

  const [updateInstallation] = useUpdateInstallationMutation();
  const [createInstallation] = useCreateInstallationMutation();

  const [triggerGetBrandModels, { data: brandModelsData }] = useLazyGetBrandModelsQuery();
  const models = brandModelsData?.brand_models || [];

  const [brandId, setBrandId] = useState(selectedSurveyData?.brand?.id);
  const [natureOfWork, setNatureOfWork] = useState("");

  const [outStationForInstall, setOutStationForInstall] = useState(false)
  const [outstationClient, setOutstationClient] = useState(false)

  const sendReportsToApi = isEditMode
    ? selectedInstallationData?.brand?.send_reports_to_api
    : selectedSurveyData?.brand?.send_reports_to_api;

  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;
  const isMisUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis"].includes(role.role_type)
  );
  const isAdminUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user", "management"].includes(role.role_type)
  );

  const isManager = user?.roles?.some((role: { role_type: string }) =>
    ["city_manager", "zonal_manager"].includes(role.role_type)
  );

  const isSuperUser = (user: any) => {
    return isUserHasRoleTypes(user, ["super_user"]);
  };

  const isFinanceController = (user: any) => {
    return isUserHasRoleTypes(user, ["finance_controller"]);
  };

  const isEditUser = isAdminUser || isMisUser;

  const isMisUserEditingCompletedInstallation =
    isEditMode && isMisUser && selectedInstallationData?.status === "completed";

  const extraCableStatus = Form.useWatch("extra_cable_payment_status", form);
  const sowStatus = Form.useWatch("sow_status", form);

  const isApproved = selectedInstallationData?.approved_at;
  const isCompleted = selectedInstallationData?.status == "completed";
  const outstationDistRequired = isEditMode ?
    selectedInstallationData?.brand?.outstation_distance_required
    : selectedSurveyData?.brand?.outstation_distance_required;

  const handleNatureOfWorkChange = (value: string) => {
    setNatureOfWork(value);
  };

  useEffect(() => {
    if (selectedInstallationData?.nature_of_work) {
      setNatureOfWork(selectedInstallationData.nature_of_work);
    }
  }, [selectedInstallationData]);

  useEffect(() => {
    if (isEditMode) {
      setOutStationForInstall(selectedInstallationData?.is_outstation);
    } else {
      setOutStationForInstall(selectedSurveyData?.is_outstation);
    }
  }, [selectedInstallationData, selectedSurveyData]);

  useEffect(() => {
    if (isEditMode) {
      setOutstationClient(selectedInstallationData?.is_outstation_for_client);
    } else {
      setOutstationClient(selectedSurveyData?.is_outstation_for_client);
    }
  }, [selectedInstallationData, selectedSurveyData]);


  useEffect(() => {
    let fieldsValue: any;
    if (isEditMode === true) {
      let scheduled_install_date_time = null;
      let planned_installation_end_date_time = null;
      let planned_installation_start_date_time = null;
      let invoice_date = null;
      let commission_date = null;
      if (selectedInstallationData?.scheduled_install_date_time) {
        const localDateTime = localizeDateTime(
          selectedInstallationData?.scheduled_install_date_time
        );
        const [datePart, timePart] = localDateTime.split(", ");
        const [day, month, year] = datePart.split("/");
        const [hours, minutes, seconds] = timePart.split(":");

        scheduled_install_date_time = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      if (selectedInstallationData?.planned_installation_start_date_time) {
        const localDateTime = localizeDateTime(
          selectedInstallationData?.planned_installation_start_date_time
        );
        const [datePart, timePart] = localDateTime.split(", ");
        const [day, month, year] = datePart.split("/");
        const [hours, minutes, seconds] = timePart.split(":");

        planned_installation_start_date_time = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      if (selectedInstallationData?.planned_installation_end_date_time) {
        const localDateTime = localizeDateTime(
          selectedInstallationData?.planned_installation_end_date_time
        );
        const [datePart, timePart] = localDateTime.split(", ");
        const [day, month, year] = datePart.split("/");
        const [hours, minutes, seconds] = timePart.split(":");

        planned_installation_end_date_time = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      if (selectedInstallationData?.invoice_date) {
        const localDateTime = localizeDateTime(selectedInstallationData?.invoice_date);
        const [datePart, timePart] = localDateTime.split(", ");
        const [day, month, year] = datePart.split("/");
        invoice_date = `${year}-${month}-${day}`;
      }
      if (selectedInstallationData?.commission_date) {
        const localDateTime = localizeDateTime(selectedInstallationData?.commission_date);
        const [datePart, timePart] = localDateTime.split(", ");
        const [day, month, year] = datePart.split("/");
        commission_date = `${year}-${month}-${day}`;
      }

      fieldsValue = {
        ...selectedInstallationData,
        booking_id: selectedInstallationData?.booking?.id,
        survey_id: selectedInstallationData?.survey?.id,

        install_partner_id: selectedInstallationData?.install_partner?.id,
        assigned_partner_user_id: selectedInstallationData?.assigned_partner_user?.id,
        assigned_field_agent1_id: selectedInstallationData?.assigned_field_agent1?.id,
        assigned_field_agent2_id: selectedInstallationData?.assigned_field_agent2?.id,
        scheduled_install_date_time: scheduled_install_date_time,
        planned_installation_start_date_time: planned_installation_start_date_time,
        planned_installation_end_date_time: planned_installation_end_date_time,
        invoice_date: invoice_date,
        commission_date: commission_date
      };
      brandModels = brands?.find(
        (brand: any) => brand?.id === selectedInstallationData?.brand?.id
      )?.models;
    } else {
      fieldsValue = {
        ...selectedSurveyData,
        booking_id: selectedSurveyData?.booking?.id,
        survey_id: selectedSurveyData?.id
      };
      brandModels = brands?.find(
        (brand: any) => brand?.id === selectedSurveyData?.brand?.id
      )?.models;
    }
    form.setFieldsValue({
      ...fieldsValue,
      booking_id: fieldsValue.booking_id,
      survey_id: fieldsValue.survey_id,
      city_id: fieldsValue?.city?.id,
      zone_id: fieldsValue?.zone?.id,
      state_id: fieldsValue?.state?.id,
      assigned_city_manager_id: fieldsValue?.assigned_city_manager?.id,
      brand_model_id: fieldsValue?.brand_model?.id
    });
  }, [selectedSurveyData, selectedInstallationData]);

  useEffect(() => {
    const brandId = selectedInstallationData?.brand?.id || selectedSurveyData?.brand?.id;
    if (brandId) {
      triggerGetBrandModels({ all_pages: true, brand_id: brandId });
    }
  }, [selectedSurveyData, selectedInstallationData]);

  const onFinish = (values: any) => {
    setLoading(true);
    if (isEditMode !== true) {
      addInstallation(values, setLoading, form);
    } else {
      onUpdateInstallation(values, setLoading, form, installationId);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    api.info({
      message: "Operation failed",
      description: (
        <div>
          {errorInfo?.errorFields?.map((error: any) => (
            <p key={error?.name[0]}>
              {error?.name[0]} : {error?.errors?.join(",")}
            </p>
          ))}
        </div>
      ),
      placement: "top"
    });
    console.log("Failed:", errorInfo);
  };

  const addInstallation = async (values: any, setLoading: any, form: any) => {
    try {
      const scheduled_install_date_time = new Date(
        values?.scheduled_install_date_time
      ).toISOString();

      if (values?.planned_installation_start_date_time) {
        values["planned_installation_start_date_time"] = new Date(
          values?.planned_installation_start_date_time
        ).toISOString();
      }
      if (values?.planned_installation_end_date_time) {
        values["planned_installation_end_date_time"] = new Date(
          values?.planned_installation_end_date_time
        ).toISOString();
      }

      const data = {
        ...values,
        booking_id: selectedSurveyData?.booking?.id,
        survey_id: selectedSurveyData?.id,
        scheduled_install_date_time
      };
      await createInstallation({ installation: data }).unwrap();
      api.success({
        message: "Installation created successfully!",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      navigate("/installations");
    } catch (error: any) {
      api.info({
        message: "Installation creation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateInstallation = async (values: any, setLoading: any, form: any, id: any) => {
    try {
      if (values?.scheduled_install_date_time) {
        values["scheduled_install_date_time"] = new Date(
          values?.scheduled_install_date_time
        ).toISOString();
      }

      if (values?.planned_installation_start_date_time) {
        values["planned_installation_start_date_time"] = new Date(
          values?.planned_installation_start_date_time
        ).toISOString();
      }

      if (values?.planned_installation_end_date_time) {
        values["planned_installation_end_date_time"] = new Date(
          values?.planned_installation_end_date_time
        ).toISOString();
      }

      const data = {
        ...values,
        booking_id: selectedInstallationData?.booking?.id,
        survey_id: selectedInstallationData?.survey?.id
      };
      await updateInstallation({ id, installation: data }).unwrap();
      api.success({
        message: "Installation updated successfully!",
        placement: "top"
      });
      setLoading(false);
      form.resetFields();
      if (window.history.length > 1) {
        history.back();
      } else {
        navigate("/installations");
      }
    } catch (error: any) {
      api.info({
        message: "Update Installation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const handlePartnerChange = (value: string) => {
    form.setFieldsValue({
      survey_partner_id: value,
      assigned_field_agent1_id: null,
      assigned_field_agent2_id: null,
      assigned_partner_user_id: null
    });
    setPartnerId(value);
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <>
      {contextHolder}
      <div style={{ paddingBottom: 50 }}>
        <Row style={{ marginBottom: 20 }}>
          <Col
            sm={24}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            {isEditMode ? <h2>Update Installation</h2> : <h2>Create Installation</h2>}
            <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
              Back
            </Button>
          </Col>
        </Row>

        <Form
          name="brand_form"
          form={form}
          initialValues={{ remember: true }}
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 11 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          {!isFinanceController(user) && !isMisUserEditingCompletedInstallation && (
            <>
              <Typography style={{ fontWeight: 500 }}>Customer Details</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <InputElement
                    label="Customer Name"
                    name="customer_name"
                    disabled={isManager && sendReportsToApi}
                    rules={[{ required: true, message: "Please Enter Customer Name" }]}
                  />
                  <InputElement label="Email" name="email" />
                  <InputElement
                    label="GST Number"
                    name="gst"
                    rules={[
                      { min: 15, message: "GST Number must be minimum 15 digit." },
                      { max: 15, message: "GST Number be maximum 15 digit." }
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <ContactNumberInputElement disabled={isManager && sendReportsToApi} />
                  <AltContactNumberInputElement disabled={isManager && sendReportsToApi} />
                </Col>
              </Row>

              <Typography style={{ fontWeight: 500 }}>Vehicle Details</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <BrandModelSelect brandModels={models} disabled={isManager && sendReportsToApi} />
                  <InputElement label="Model Variant" name="model_variant" />
                  <InputElement label="Model Colour" name="model_colour" />
                </Col>
                <Col span={12}>
                  <InputElement label="Model Description" name="model_description" />
                  <InputElement label="Model Int Color" name="model_int_color" />
                  <InputElement label="Model Ext Color" name="model_ext_color" />
                  <InputElement
                    label="UML SOW Remarks"
                    disabled={!isSuperUser(user)}
                    name="uml_sow_install_remarks"
                  />
                </Col>
              </Row>

              <Typography style={{ fontWeight: 500 }}>Address and Schedule</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <InputElement
                    label="Booking Address"
                    name="booking_address"
                    rules={[{ required: true, message: "Please Enter Booking Address" }]}
                    disabled
                  />
                  <InputElement
                    label="Survey Address Line1"
                    name="address_line1"
                    disabled={isManager && sendReportsToApi}
                  />
                  <InputElement
                    label="Survey Locality"
                    name="locality"
                    disabled={isManager && sendReportsToApi}
                  />
                  <CustomerCitySelect disabled={isManager && sendReportsToApi} />
                  <CitySelect
                    onChange={(cityLocationId: any) => onCityChange(cityLocationId)}
                    disabled={isManager && sendReportsToApi}
                  />
                  <StateSelect disabled={isManager && sendReportsToApi} />
                  <Form.Item
                    label="Outstation for installer ?"
                    name="is_outstation"
                    rules={[{ required: true, message: "Please select outstation" }]}
                  >
                    <Select
                      placeholder="Select Outstation"
                      allowClear
                      showSearch
                      onChange={(value) => setOutStationForInstall(value)}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                  {outStationForInstall && <Form.Item
                    label="Category"
                    name="category"
                    rules={[{ required: true, message: "Please select outstation" }]}
                  >
                    <Select
                      placeholder="Select Category"
                      allowClear
                      showSearch
                      disabled={isManager && (isApproved || isCompleted)}
                      onChange={(value) => setOutStationForInstall(value)}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={categoryOptions}
                    ></Select>
                  </Form.Item>}
                  <Form.Item
                    label="Outstation for Client ?"
                    name="is_outstation_for_client"
                    rules={[{ required: true, message: "Please select outstation" }]}
                  >
                    <Select
                      placeholder="Select Outstation"
                      allowClear
                      showSearch
                      onChange={(value) => setOutstationClient(value)}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                  {outstationClient &&
                    <NumberInputElement
                      disabled={isManager && (isApproved || isCompleted)}
                      label="Distance (in kms)"
                      name="outstation_distance"
                      rules={[
                        {
                          required: outstationDistRequired,
                          message: "Please Enter Outstation Distance",
                        },
                      ]} />}
                  <InputElement label="Site POC Name" name="site_poc_name" />
                </Col>
                <Col span={12}>
                  <PincodeInputElement disabled={isManager && sendReportsToApi} />
                  <Form.Item
                    label="Scheduled Install DateTime"
                    name="scheduled_install_date_time"
                    rules={[{ required: true, message: "Please select install date Time" }]}
                  >
                    <Input type="datetime-local" />
                  </Form.Item>
                  {sendReportsToApi && (
                    <>
                      <Form.Item
                        label="Proposed time of start of installation"
                        name="planned_installation_start_date_time"
                        rules={[
                          { required: true, message: "Please select proposed install start time" }
                        ]}
                      >
                        <Input type="datetime-local" />
                      </Form.Item>
                      <Form.Item
                        label="Proposed installation end time"
                        name="planned_installation_end_date_time"
                        rules={[
                          { required: true, message: "Please select proposed install end time" }
                        ]}
                      >
                        <Input type="datetime-local" />
                      </Form.Item>
                    </>
                  )}
                  <InputElement
                    label="Survey Address Line2"
                    name="address_line2"
                    disabled={isManager && sendReportsToApi}
                  />
                  <ZoneSelect disabled={!isEditUser} required />
                  <InputElement label="Latitude" name="latitude" />
                  <InputElement label="Longitude" name="longitude" />
                  <InputElement label="Site POC Contact" name="site_poc_contact" />
                </Col>
              </Row>

              <Typography style={{ fontWeight: 500 }}>Assignment</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <CityManagerSelect />
                  <DropDownSelectProps
                    label="Assigned Field Agent1"
                    name="assigned_field_agent1_id"
                    records={fieldAgents}
                    placeHolder="Select field agent"
                  />
                  <DropDownSelectProps
                    label="Assigned Field Agent2"
                    name="assigned_field_agent2_id"
                    records={fieldAgents}
                    placeHolder="Select field agent"
                  />
                </Col>
                <Col span={12}>
                  <DropDownSelectProps
                    label="Install Partner"
                    required
                    name="install_partner_id"
                    records={partnerData?.partners}
                    placeHolder="Select partner"
                    onSelect={handlePartnerChange}
                  />
                  <DropDownSelectProps
                    label="Assigned Partner User"
                    name="assigned_partner_user_id"
                    records={partnerManager}
                    placeHolder="Select partner user"
                  />
                  <InputElement label="Note For Agent" name="note_for_agent" />
                </Col>
              </Row>

              <Typography style={{ fontWeight: 500 }}>Other</Typography>
              <Row gutter={16}>
                <Col span={12}>
                  <CheckBoxInput
                    label="Customer Survey Confirmed"
                    name="customer_survey_confirmed"
                  />
                  <CheckBoxInput
                    label="Customer Install Confirmed"
                    name="customer_install_confirmed"
                  />
                  <InputElement label="Summary Stage1" name="summary_stage1" />
                  <InputElement label="Summary Stage2" name="summary_stage2" />
                  <InputElement label="Charger Capacity" name="charger_capacity" />
                  <InputElement label="Charger Make Type" name="charger_make_type" />
                  {isSuperUser(user) && (
                    <InputElement label="Cable Length" name="cable_length_new" />
                  )}
                  <InputElement label="Sanctioned Load" name="sanctioned_load" />
                  <InputElement label="Average Load Utilised" name="avg_load_utilized" />
                  <InputElement label="VDC" name="vdc" />
                  <InputElement label="MDI" name="mdi" />
                  <InputElement label="Cable Gauge" name="cable_gauge" />
                  <Form.Item
                    label="Nature of Work"
                    name="nature_of_work"
                    rules={[{ required: true, message: "Please select nature of work" }]}
                  >
                    <Select
                      placeholder="Select Nature of Work"
                      allowClear
                      showSearch
                      onChange={handleNatureOfWorkChange}
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={natureOfWorkOptions}
                    ></Select>
                  </Form.Item>
                  {natureOfWork === "reinstallation" && (
                    <>
                      <InputElement
                        label="Old Cable used (length)"
                        name="old_cable_used_length"
                        rules={[
                          { required: true, message: "Please Enter Old Cable used (length)" }
                        ]}
                      />
                      <InputElement
                        label="New Cable used (length)"
                        name="new_cable_used_length"
                        rules={[
                          { required: true, message: "Please Enter New Cable used (length)" }
                        ]}
                      />
                      <InputElement
                        label="No. of old MCB /RCBO used"
                        name="number_of_old_mcb_rcbo_used"
                        rules={[
                          { required: true, message: "Please Enter No. of old MCB /RCBO used" }
                        ]}
                      />
                      <InputElement
                        label="No. of New MCB /RCBO used"
                        name="number_of_new_mcb_rcbo_used"
                        rules={[
                          { required: true, message: "Please Enter No. of new MCB /RCBO used" }
                        ]}
                      />
                    </>
                  )}
                  <Form.Item label="SOW completed (Yes/No)?" name="is_sow_completed">
                    <Select
                      placeholder="Select Option"
                      allowClear
                      showSearch
                      disabled
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                  <Form.Item
                    label="Date of payment for extra cable if applicable"
                    name="extra_cable_payment_date"
                    rules={
                      extraCableStatus == "completed"
                        ? [
                          {
                            required: true,
                            message: "Please Enter Date of payment for extra cable if applicable"
                          }
                        ]
                        : []
                    }
                  >
                    <Input type="date" />
                  </Form.Item>
                  <Form.Item
                    label="SOW completion date"
                    name="sow_completion_date"
                    rules={
                      sowStatus == "completed"
                        ? [
                          {
                            required: true,
                            message: "Please Enter SOW completion date"
                          }
                        ]
                        : []
                    }
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <InputElement label="Company Name" name="company_name" />

                  <CheckBoxInput
                    label="Customer Ok With Recording"
                    name="customer_ok_with_recording"
                  />
                  <InputElement label="Remarks" name="remarks" />

                  <CheckBoxInput label="Extra Cable Customer Pay" name="extra_cable_customer_pay" />

                  <Form.Item
                    label="Is this client (OEM) paid - Installation ?"
                    name="customer_billing"
                    rules={[{ required: true, message: "Please select option" }]}
                  >
                    <Select
                      placeholder="Please select option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={customerBillingOptions}
                    ></Select>
                  </Form.Item>

                  <Form.Item
                    label="Install Type"
                    name="install_type"
                    rules={[{ required: true, message: "Please select install type" }]}
                  >
                    <Select
                      placeholder="Select Install Type"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={isOldIstallation ? OldInstallTypeOptions : installTypeOptions}
                    ></Select>
                  </Form.Item>

                  <Form.Item
                    label="Any extra material used at installation ?"
                    name="is_extra_material_used"
                    rules={[{ required: true, message: "Please select" }]}
                  >
                    <Select
                      placeholder="Select Option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>

                  <Form.Item
                    label="Is there a no show during installation ?"
                    name="is_no_show_during_installation"
                    rules={[{ required: true, message: "Please select" }]}
                  >
                    <Select
                      placeholder="Select Option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(inputValue, option: any) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      options={yesNoOptions}
                    ></Select>
                  </Form.Item>
                  <Form.Item label="Commission Date" name="commission_date">
                    <Input type="date" />
                  </Form.Item>
                  <InputElement label="Engine Number" name="engine_number" />
                  <CheckBoxInput label="Survey Done Same Day" name="survey_done_same_day" />
                  <InputElement
                    label="Charger Type"
                    name="obc"
                    disabled={!(isSuperUser || isMisUser)}
                  />
                  <Form.Item
                    label="Status of payment for extra cable"
                    name="extra_cable_payment_status"
                  >
                    <Select
                      placeholder="Please select option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      options={completionStatusOptions}
                    ></Select>
                  </Form.Item>
                  <Form.Item label="Status of customer side SOW" name="sow_status">
                    <Select
                      placeholder="Please select option"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option: any) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      options={completionStatusOptions}
                    ></Select>
                  </Form.Item>
                  <Form.Item
                    label="Installation readiness information date"
                    name="installation_readiness_information_date"
                    rules={[
                      {
                        required: true,
                        message: "Please Enter Installation readiness information date"
                      }
                    ]}
                  >
                    <Input
                      disabled={
                        (!extraCableStatus && !sowStatus) ||
                        extraCableStatus === "pending" ||
                        sowStatus === "pending"
                      }
                      type="date"
                    />
                  </Form.Item>
                  {natureOfWork === "reinstallation" && (
                    <>
                      <InputElement
                        label="No. of old MCB /RCBO box used"
                        name="number_of_old_mcb_rcbo_box_used"
                        rules={[
                          { required: true, message: "Please Enter No. of old MCB /RCBO box used" }
                        ]}
                      />
                      <InputElement
                        label="No. of New MCB /RCBO Box used"
                        name="number_of_new_mcb_rcbo_box_used"
                        rules={[
                          { required: true, message: "Please Enter No. of New MCB /RCBO Box used" }
                        ]}
                      />
                      <InputElement
                        label="Remarks (any other material used etc)"
                        name="nature_of_install_remarks"
                        rules={[
                          {
                            required: true,
                            message: "Please Enter Remarks (any other material used etc)"
                          }
                        ]}
                      />
                    </>
                  )}
                </Col>
              </Row>
            </>
          )}
          {(isFinanceController(user) || isSuperUser(user)) &&
            !isMisUserEditingCompletedInstallation && (
              <>
                <Typography style={{ fontWeight: 500 }}>Invoice Details</Typography>
                <Row gutter={16}>
                  <Col span={12}>
                    <InputElement label="Invoice Amount" name="invoice_amount" />
                    <InputElement label="Invoice Number" name="invoice_number" />
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Please select invoice date" name="invoice_date">
                      <Input
                        type="date"
                        max={getCurrentDateTime()} // Restrict future dates
                      />
                    </Form.Item>
                    <InputElement label="Pricing Per Meter" name="pricing_per_meter" />
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <InputElement
                      label="Complementary Cable Length"
                      name="complementary_cable_length"
                    />
                  </Col>
                </Row>
              </>
            )}

          {isMisUserEditingCompletedInstallation && (
            <Row gutter={16}>
              <Col span={12}>
                <InputElement label="Survey Address Line1" name="address_line1" />
                <CustomerCitySelect />
                <StateSelect />
                <Form.Item
                  label="Outstation for installer ?"
                  name="is_outstation"
                  rules={[{ required: true, message: "Please select outstation" }]}
                >
                  <Select
                    placeholder="Select Outstation"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    onChange={(value) => setOutStationForInstall(value)}
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <PincodeInputElement />
                <InputElement label="Survey Address Line2" name="address_line2" />
                <Form.Item
                  label="Outstation for Client ?"
                  name="is_outstation_for_client"
                  rules={[{ required: true, message: "Please select outstation" }]}
                >
                  <Select
                    placeholder="Select Outstation"
                    allowClear
                    showSearch
                    onChange={(value) => setOutstationClient(value)}
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={yesNoOptions}
                  ></Select>
                </Form.Item>
                {outstationClient &&
                  <NumberInputElement label="Distance (in kms)"
                    disabled={isManager && (isApproved || isCompleted)}
                    name="outstation_distance"
                    rules={[
                      {
                        required: outstationDistRequired,
                        message: "Please Enter Outstation Distance",
                      },
                    ]} />}
                {outStationForInstall && <Form.Item
                  label="Category"
                  name="category"
                  rules={[{ required: true, message: "Please select category" }]}
                >
                  <Select
                    placeholder="Select Category"
                    allowClear
                    showSearch
                    disabled={isManager && (isApproved || isCompleted)}
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={categoryOptions}
                  ></Select>
                </Form.Item>}
              </Col>
            </Row>

          )}

          <Form.Item wrapperCol={{ offset: 19, span: 24 }}>
            <Row>
              <Col>
                <Space size={"middle"}>
                  <Button
                    type="default"
                    onClick={() => {
                      form.resetFields();
                      onGoBack();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={
                      isFinanceController(user)
                        ? false
                        : loading ||
                        (isEditMode &&
                          !isAdminUser &&
                          !isMisUserEditingCompletedInstallation &&
                          selectedInstallationData?.status === "completed")
                    }
                  >
                    {isEditMode ? "Update" : "Add"}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default InstallationForm;
